import { Contract, partnerOf } from "@ravalmatic/bridge-engine/core";
import { Button } from "@ravalmatic/catafractum-utils";
import { isLocalPlayerSeat } from "../PlayerSeats";

export class InfoContractContainer extends Phaser.GameObjects.Container {

    private readonly contractRow: Phaser.GameObjects.Container;
    private readonly contractBadgeSuit: Phaser.GameObjects.Image;
    private readonly contractBadge: Phaser.GameObjects.Text;
    private readonly noTrumpBadgeLabel: Phaser.GameObjects.Text;
    private readonly strainLabel: Phaser.GameObjects.Text;
    private readonly contractDescription: Phaser.GameObjects.Text;
    private readonly contractTarget: Phaser.GameObjects.Text;
    private readonly contractTargetSuffix: Phaser.GameObjects.Text;
    private readonly declarerLabel: Phaser.GameObjects.Text;
    private readonly declarerDescription: Phaser.GameObjects.Text;
    private readonly goalContainer: Phaser.GameObjects.Container;
    private readonly goalPrefix: Phaser.GameObjects.Text;
    private readonly goalNumber: Phaser.GameObjects.Text;
    private readonly goalSuffix: Phaser.GameObjects.Text;
    private readonly goalDescription: Phaser.GameObjects.Text;

    constructor(scene: Phaser.Scene, onBack: () => void, continueButtonY: number) {

        super(scene);

        const badgeBackground = new Phaser.GameObjects.Graphics(scene);
        badgeBackground.fillStyle(0xffe5b8, 1).fillRoundedRect(-174, -125, 112, 54, 15);
        this.contractBadge = new Phaser.GameObjects.Text(scene, -145, -98, "", {
            color: "#202630",
            fontFamily: "Baloo2-Bold",
            fontSize: "34px"
        }).setOrigin(.5);
        this.contractBadgeSuit = new Phaser.GameObjects.Image(
            scene, -96, -98, "texture_atlas_1", "clubs_icon"
        ).setScale(.16);
        this.noTrumpBadgeLabel = new Phaser.GameObjects.Text(scene, -96, -98, "NT", {
            color: "#202630",
            fontFamily: "Baloo2-Bold",
            fontSize: "25px"
        }).setOrigin(.5);
        this.strainLabel = new Phaser.GameObjects.Text(scene, -48, -98, "", {
            color: "#00d9ed",
            fontFamily: "Baloo2-Bold",
            fontSize: "22px"
        }).setOrigin(0, .5);
        const title = new Phaser.GameObjects.Image(scene, 0, -164, "texture_atlas_1", "contract_help_title");

        const contractHeading = this.createHeading(scene, "THE CONTRACT:", -53);
        this.contractDescription = this.createBody(scene, "", -22);
        this.contractTarget = new Phaser.GameObjects.Text(scene, -174, 28, "", {
            color: "#ffd747",
            fontFamily: "Baloo2-Bold",
            fontSize: "44px"
        }).setOrigin(0, .5);
        this.contractTargetSuffix = this.createBody(scene, "of the 13 tricks.", 17);

        this.declarerLabel = this.createHeading(scene, "", 49);
        this.declarerDescription = new Phaser.GameObjects.Text(scene, -174, 74, "", {
            color: "#ffffff",
            fontFamily: "Baloo2-Regular",
            fontSize: "18px"
        }).setOrigin(0, 0);

        const goalBackground = new Phaser.GameObjects.Graphics(scene);
        goalBackground.fillStyle(0x0879b8, 1).fillRoundedRect(-188, 102, 376, 99, 16);
        goalBackground.fillStyle(0x073c77, 1).fillRoundedRect(-185, 105, 370, 93, 14);
        const goalHeading = this.createHeading(scene, "YOUR GOAL:", 114, -145);
        this.goalPrefix = this.createBody(scene, "Win", 143, -145, 418);
        this.goalNumber = new Phaser.GameObjects.Text(scene, -91, 154, "", {
            color: "#ffd747",
            fontFamily: "Baloo2-Bold",
            fontSize: "38px"
        }).setOrigin(.5);
        this.goalSuffix = this.createBody(scene, "tricks on defense", 143, -66, 418);
        this.goalDescription = this.createBody(scene, "to defeat the contract.", 166, -145, 418);

        const backButton = new Button(scene, 0, continueButtonY, "texture_atlas_1", "btn_back_contract");
        backButton.setScale(.9);
        backButton.setInflationTween(1.05, .9, .9);
        backButton.onUp(onBack, this);

        this.contractRow = new Phaser.GameObjects.Container(scene, 0, 0, [
            badgeBackground,
            this.contractBadge,
            this.contractBadgeSuit,
            this.noTrumpBadgeLabel,
            this.strainLabel
        ]);
        const content = new Phaser.GameObjects.Container(scene, 0, -40, [
            title,
            this.contractRow,
            contractHeading,
            this.contractDescription,
            this.contractTarget,
            this.contractTargetSuffix,
            this.declarerLabel,
            this.declarerDescription
        ]);
        this.goalContainer = new Phaser.GameObjects.Container(scene, 0, -20, [
            goalBackground,
            goalHeading,
            this.goalPrefix,
            this.goalNumber,
            this.goalSuffix,
            this.goalDescription
        ]);
        this.add([content, this.goalContainer, backButton]);
        this.setVisible(false);
    }

    public showContract(contract: Contract): void {

        const tricksToMake = contract.level + 6;
        const declarerNames = ["NORTH", "EAST", "SOUTH", "WEST"];
        const suitNames = ["CLUBS", "DIAMONDS", "HEARTS", "SPADES", "NO TRUMP"];
        const suitFrames = ["clubs_icon", "diamonds_icon", "hearts_icon", "spades_icon"];
        const declarerSide = contract.declarer % 2 === 0 ? "North-South" : "East-West";
        const nsDeclares = contract.declarer % 2 === 0;
        const dummyName = ["North", "East", "South", "West"][partnerOf(contract.declarer)];

        this.contractBadge.setText(`${contract.level}`);
        this.contractBadgeSuit.setVisible(contract.strain !== 4);
        if (contract.strain !== 4) {
            this.contractBadgeSuit.setFrame(suitFrames[contract.strain]);
        }
        this.noTrumpBadgeLabel.setVisible(contract.strain === 4);
        this.strainLabel.setText(
            contract.strain === 4 ? "NO TRUMP" : `TRUMP: ${suitNames[contract.strain]}`
        );
        this.contractRow.setX(-(-174 + this.strainLabel.x + this.strainLabel.displayWidth) / 2);
        this.contractDescription.setText(`${declarerSide} must win at least`);
        this.contractTarget.setText(`${tricksToMake}`);
        this.contractTargetSuffix.setX(
            this.contractTarget.x + this.contractTarget.displayWidth + 8
        );
        this.declarerLabel.setText(`DECLARER: ${declarerNames[contract.declarer]}`);
        this.declarerDescription.setText(
            `${dummyName} is the dummy.${isLocalPlayerSeat(contract.declarer) ? "\nYou play both hands." : ""}`
        );
        this.goalContainer.setVisible(true);
        this.goalPrefix.setText(nsDeclares && tricksToMake === 13 ? "Win all" : "Win");
        this.goalNumber.setText(`${nsDeclares ? tricksToMake : 14 - tricksToMake}`);
        this.goalSuffix.setText(nsDeclares ? "tricks with North." : "tricks on defense");
        const goalTexts = [this.goalPrefix, this.goalNumber, this.goalSuffix];
        goalTexts.forEach(text => text.setScale(1));
        const goalWidth = goalTexts.reduce((width, text) => width + text.displayWidth, 0);
        const goalScale = Math.min(1, (310 - 16) / goalWidth);
        goalTexts.forEach(text => text.setScale(goalScale));
        this.goalNumber.setX(
            this.goalPrefix.x + this.goalPrefix.displayWidth + 8 + this.goalNumber.displayWidth / 2
        );
        this.goalSuffix.setX(this.goalNumber.x + this.goalNumber.displayWidth / 2 + 8);
        this.goalDescription.setText("to defeat the contract.");
        this.goalDescription.setVisible(!nsDeclares);
    }

    private createHeading(
        scene: Phaser.Scene, text: string, y: number, x = -174
    ): Phaser.GameObjects.Text {

        return new Phaser.GameObjects.Text(scene, x, y, text, {
            color: "#00d9ed",
            fontFamily: "Baloo2-Bold",
            fontSize: "23px"
        }).setOrigin(0, 0);
    }

    private createBody(
        scene: Phaser.Scene, text: string, y: number, x = -174, wrapWidth = 348
    ): Phaser.GameObjects.Text {

        return new Phaser.GameObjects.Text(scene, x, y, text, {
            color: "#ffffff",
            fontFamily: "Baloo2-Regular",
            fontSize: "22px",
            wordWrap: { width: wrapWidth }
        }).setOrigin(0, 0);
    }
}
